import { useAuthenticator } from "@aws-amplify/ui-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RpiApiProvider } from "@rpi/openapi-api";
import { Auth } from "aws-amplify";
import React from "react";
import { Outlet, useLocation, useMatch } from "react-router-dom";
import { graphqlClient } from "../App";
import { RpiModalsProvider } from "../components/modals/modals";
import { Environment } from "../env/Environment";
import { useAuthUser } from "../hooks/useAuthUser";
import { getRoutePath, NavigateRoute } from "../hooks/useNavigateRoute";
import { RpiNotificationsProvider } from "../hooks/useRpiNotifications";
import { useRumPageTracker } from "../hooks/useRumPageTracker";
import { createQueryClient } from "../utils/query.util";
import { RouteLoadingView } from "../views/error/RouteLoadingView";

type AuthRoute =
  | "authenticated"
  | "confirmResetPassword"
  | "confirmSignIn"
  | "confirmSignUp"
  | "confirmVerifyUser"
  | "forceNewPassword"
  | "idle"
  | "resetPassword"
  | "setup"
  | "setupTOTP"
  | "signIn"
  | "signOut"
  | "signUp"
  | "verifyUser";

type AuthStatus = "configuring" | "authenticated" | "unauthenticated";

const logAuthStatus = (isPrivate: boolean, status: string) => {
  if (!Environment.isProduction) {
    console.log(
      `%cStatus: ${status} (${isPrivate ? "PRIVATE" : "PUBLIC"})`,
      `color: ${isPrivate ? "#f7751d" : "#9ff71d"}`
    );
  }
};

const PublicLayout: React.FC = () => {
  const [queryClient] = React.useState(createQueryClient);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const { state } = useLocation();

  React.useEffect(() => logAuthStatus(false, authStatus), [authStatus]);
  useRumPageTracker();

  const children = React.useMemo(() => {
    const from = state?.from as Location | undefined;

    switch (authStatus as AuthStatus) {
      case "authenticated":
        return <NavigateRoute {...(from?.pathname ? { to: from.pathname } : { route: ["home"] })} state={undefined} />;

      default:
        break;
    }

    return null;
  }, [authStatus, state]);

  return <QueryClientProvider client={queryClient}>{children || <Outlet />}</QueryClientProvider>;
};

function useOnboardingRoute() {
  const user = useAuthUser();
  const { state } = useLocation();
  const isOnboardingComplete = user.dashboardUser?.customer?.isOnboardingComplete;
  const onboardingPathMatch = useMatch("onboarding");
  const navigatedFromOnboarding = state?.from?.pathname === getRoutePath("onboarding");

  let onboardingRouteGuard: React.ReactElement | undefined = undefined;

  if (user.isDashboardUserLoading) {
    onboardingRouteGuard = <RouteLoadingView />;
  } else if (
    isOnboardingComplete === false &&
    !user.isDashboardUserFetching &&
    location.pathname !== getRoutePath("onboarding")
  ) {
    onboardingRouteGuard = <NavigateRoute route={["onboarding"]} />;
  } else if (isOnboardingComplete === true && onboardingPathMatch && !navigatedFromOnboarding) {
    onboardingRouteGuard = <NavigateRoute route={["home"]} />;
  }

  return onboardingRouteGuard;
}

const PrivateLayoutInner: React.FC = () => {
  const { route } = useAuthenticator((context) => [context.route]);
  const location = useLocation();

  React.useEffect(() => logAuthStatus(true, route), [route]);
  useRumPageTracker();

  const onboardingRouteGuard = useOnboardingRoute();

  const children = React.useMemo(() => {
    switch (route as AuthRoute) {
      case "authenticated": {
        if (onboardingRouteGuard) {
          return onboardingRouteGuard;
        }
        break;
      }

      /**
       * Used as `unauthenticated`. Use cases:
       * - On load: `idle` -> `setup` | `authenticated`
       * - On sign out: `authenticated` -> `signOut` -> `setup`
       */
      case "setup":
      case "setupTOTP":
      case "signIn":
      case "confirmSignIn":
      case "confirmSignUp":
      case "confirmResetPassword":
      case "confirmVerifyUser":
      case "forceNewPassword":
      case "verifyUser": {
        return <NavigateRoute route={["auth:sign-in"]} state={{ from: location }} />;
      }

      case "signUp": {
        return <NavigateRoute route={["auth:sign-up"]} state={{ from: location }} />;
      }

      case "signOut": {
        return <NavigateRoute to="/" state={{ from: location }} />;
      }

      case "resetPassword": {
        return <NavigateRoute route={["auth:reset-password"]} state={{ from: location }} />;
      }

      default: {
        return <RouteLoadingView />;
      }
    }

    return null;
  }, [route, location, onboardingRouteGuard]);

  return (
    children || (
      <RpiNotificationsProvider>
        <Outlet />
      </RpiNotificationsProvider>
    )
  );
};

const PrivateLayout: React.FC = () => {
  const [queryClient] = React.useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <RpiApiProvider
        value={{
          graphqlClient,
          headers: async () => {
            const session = await Auth.currentSession();
            const jwtToken = session.getAccessToken().getJwtToken();
            return { Authorization: jwtToken };
          },
        }}
      >
        <RpiModalsProvider>
          <PrivateLayoutInner />
        </RpiModalsProvider>
      </RpiApiProvider>
    </QueryClientProvider>
  );
};

export const AppLayout = {
  Public: PublicLayout,
  Private: PrivateLayout,
};
